import { useId, useState } from 'react';
import { api, apiError } from '@/lib/api';
import { toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  Add01Icon,
  PencilEdit02Icon,
  Delete02Icon,
  Calendar03Icon,
  Target01Icon,
  Flag01Icon,
} from '@hugeicons/core-free-icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState, EmptyExplainer, StatusBadge } from '@/components/common/Bits';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { fmtDate, BENEFIT_STATUSES, MILESTONE_STATUSES } from '@/lib/constants';

// parseList lee el valor de la columna TEXT/JSON de la iniciativa. El backend lo
// expone como array JSON (json.RawMessage); toleramos también recibir un string
// serializado o un valor ausente, devolviendo siempre un array de objetos.
export function parseList(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function newId() {
  // Identificador local estable para keys y edición; no requiere formato UUID.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

// persistList guarda la lista completa serializada como string JSON en el campo
// indicado (expected_benefits | tracking_milestones) vía PUT /initiatives/:id.
async function persistList({ id, field, list, reason }) {
  await api.put(`/initiatives/${id}`, {
    data: { [field]: JSON.stringify(list) },
    reason,
  });
}

// ─────────────────────────────────────────────────────────────
// BENEFICIOS ESPERADOS
// ─────────────────────────────────────────────────────────────

function BenefitDialog({ id, list, item, onSaved, trigger }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: item?.title || '',
    metric: item?.metric || '',
    status: item?.status || BENEFIT_STATUSES[0],
    description: item?.description || '',
  });

  const reset = () =>
    setF({
      title: item?.title || '',
      metric: item?.metric || '',
      status: item?.status || BENEFIT_STATUSES[0],
      description: item?.description || '',
    });

  const save = async () => {
    if (!f.title.trim()) {
      toast.error('El beneficio necesita un título');
      return;
    }
    setSaving(true);
    try {
      const entry = {
        id: item?.id || newId(),
        title: f.title.trim(),
        metric: f.metric.trim(),
        status: f.status,
        description: f.description.trim(),
      };
      const next = item ? list.map((b) => (b.id === item.id ? entry : b)) : [...list, entry];
      await persistList({
        id,
        field: 'expected_benefits',
        list: next,
        reason: item ? 'Edición de beneficio esperado' : 'Alta de beneficio esperado',
      });
      toast.success(item ? 'Beneficio actualizado' : 'Beneficio agregado');
      setOpen(false);
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (saving) return;
        setOpen(v);
        if (v) reset();
      }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">{item ? 'Editar beneficio' : 'Agregar beneficio esperado'}</DialogTitle>
          <DialogDescription>
            Describe el resultado que la iniciativa promete entregar y su indicador o meta.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-title`} className="text-xs">
              Beneficio
            </Label>
            <Input
              id={`${uid}-title`}
              value={f.title}
              onChange={(e) => setF({ ...f, title: e.target.value })}
              placeholder="Ej: Visibilidad del portafolio"
              disabled={saving}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${uid}-metric`} className="text-xs">
                Meta / indicador
              </Label>
              <Input
                id={`${uid}-metric`}
                value={f.metric}
                onChange={(e) => setF({ ...f, metric: e.target.value })}
                placeholder="Ej: 100% del portafolio en VIA"
                disabled={saving}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${uid}-status`} className="text-xs">
                Estado
              </Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })} disabled={saving}>
                <SelectTrigger id={`${uid}-status`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BENEFIT_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-description`} className="text-xs">
              Descripción
            </Label>
            <Textarea
              id={`${uid}-description`}
              value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
              placeholder="Detalle del beneficio y cómo se mide."
              rows={3}
              disabled={saving}
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? 'Guardando…' : item ? 'Guardar cambios' : 'Agregar beneficio'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BenefitsSection({ initiative, id, canEdit, onSaved }) {
  const list = parseList(initiative.expected_benefits);

  const remove = async (item) => {
    try {
      const next = list.filter((b) => b.id !== item.id);
      await persistList({ id, field: 'expected_benefits', list: next, reason: 'Baja de beneficio esperado' });
      toast.success('Beneficio eliminado');
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Beneficios que la iniciativa persigue: el valor que debe retornar y cómo se mide. Es la lista que resume el
          propósito de la iniciativa de un vistazo.
        </p>
        {canEdit && (
          <BenefitDialog
            id={id}
            list={list}
            onSaved={onSaved}
            trigger={
              <Button size="sm" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
                <HugeiconsIcon icon={Add01Icon} className="h-4 w-4 mr-1.5" strokeWidth={1.5} aria-hidden={true} />{' '}
                Agregar beneficio
              </Button>
            }
          />
        )}
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={Target01Icon}
          title="Sin beneficios registrados"
          subtitle={
            <EmptyExplainer
              what="Los beneficios son el valor que la iniciativa promete retornar: qué mejora concreta busca lograr y con qué indicador o meta se mide (ej. cobertura del portafolio, ledger validado, tiempo de detección)."
              whyItMatters="Dejan explícito para qué existe la iniciativa y contra qué se evaluará su éxito. Es lo que se lee de un vistazo para entender el propósito y el retorno esperado."
              ifEmpty="La iniciativa avanza sin un objetivo de valor declarado. No hay una vara clara para decidir si conviene mantenerla, escalarla o detenerla."
              whenDone="Cualquier persona entiende qué valor persigue la iniciativa y cómo se medirá, alineando el trabajo con el objetivo principal de VIA."
            />
          }
        />
      ) : (
        <ul className="space-y-3">
          {list.map((b) => (
            <li key={b.id}>
              <Card className="shadow-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-sm">{b.title}</p>
                        {b.status && <StatusBadge status={b.status} />}
                      </div>
                      {b.metric && <p className="mt-1 text-xs text-secondary font-medium">Meta: {b.metric}</p>}
                      {b.description && (
                        <p className="mt-1.5 text-sm text-muted-foreground whitespace-pre-line">{b.description}</p>
                      )}
                    </div>
                    {canEdit && (
                      <div className="flex shrink-0 items-center gap-1">
                        <BenefitDialog
                          id={id}
                          list={list}
                          item={b}
                          onSaved={onSaved}
                          trigger={
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                              aria-label={`Editar beneficio ${b.title}`}
                              title="Editar">
                              <HugeiconsIcon
                                icon={PencilEdit02Icon}
                                className="h-3.5 w-3.5"
                                strokeWidth={1.5}
                                aria-hidden={true}
                              />
                            </Button>
                          }
                        />
                        <DeleteButton label={b.title} kind="beneficio" onConfirm={() => remove(b)} />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// HITOS DE SEGUIMIENTO
// ─────────────────────────────────────────────────────────────

function MilestoneDialog({ id, list, item, onSaved, trigger }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: item?.title || '',
    status: item?.status || MILESTONE_STATUSES[0],
    promoted_at: item?.promoted_at ? item.promoted_at.slice(0, 10) : '',
    description: item?.description || '',
  });

  const reset = () =>
    setF({
      title: item?.title || '',
      status: item?.status || MILESTONE_STATUSES[0],
      promoted_at: item?.promoted_at ? item.promoted_at.slice(0, 10) : '',
      description: item?.description || '',
    });

  const save = async () => {
    if (!f.title.trim()) {
      toast.error('El hito necesita un título');
      return;
    }
    setSaving(true);
    try {
      const entry = {
        id: item?.id || newId(),
        title: f.title.trim(),
        status: f.status,
        promoted_at: f.promoted_at || null,
        description: f.description.trim(),
      };
      const next = item ? list.map((m) => (m.id === item.id ? entry : m)) : [...list, entry];
      await persistList({
        id,
        field: 'tracking_milestones',
        list: next,
        reason: item ? 'Edición de hito de seguimiento' : 'Alta de hito de seguimiento',
      });
      toast.success(item ? 'Hito actualizado' : 'Hito agregado');
      setOpen(false);
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (saving) return;
        setOpen(v);
        if (v) reset();
      }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">{item ? 'Editar hito' : 'Agregar hito de seguimiento'}</DialogTitle>
          <DialogDescription>
            Registra un próximo hito con su estado y, si aplica, la fecha prevista de promoción a producción.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-title`} className="text-xs">
              Hito
            </Label>
            <Input
              id={`${uid}-title`}
              value={f.title}
              onChange={(e) => setF({ ...f, title: e.target.value })}
              placeholder="Ej: Motor de priorización"
              disabled={saving}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor={`${uid}-status`} className="text-xs">
                Estado
              </Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })} disabled={saving}>
                <SelectTrigger id={`${uid}-status`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MILESTONE_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={`${uid}-promoted`} className="text-xs">
                Promoción a producción
              </Label>
              <Input
                id={`${uid}-promoted`}
                type="date"
                value={f.promoted_at}
                onChange={(e) => setF({ ...f, promoted_at: e.target.value })}
                disabled={saving}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`${uid}-description`} className="text-xs">
              Descripción
            </Label>
            <Textarea
              id={`${uid}-description`}
              value={f.description}
              onChange={(e) => setF({ ...f, description: e.target.value })}
              placeholder="Qué resuelve el hito y su estado de avance."
              rows={3}
              disabled={saving}
            />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90">
            {saving ? 'Guardando…' : item ? 'Guardar cambios' : 'Agregar hito'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MilestonesSection({ initiative, id, canEdit, onSaved }) {
  const list = parseList(initiative.tracking_milestones);

  const remove = async (item) => {
    try {
      const next = list.filter((m) => m.id !== item.id);
      await persistList({ id, field: 'tracking_milestones', list: next, reason: 'Baja de hito de seguimiento' });
      toast.success('Hito eliminado');
      await onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Próximos hitos de la iniciativa: qué viene, en qué estado está y cuándo se promueve a producción. Es la hoja
          de ruta de seguimiento, en un solo lugar.
        </p>
        {canEdit && (
          <MilestoneDialog
            id={id}
            list={list}
            onSaved={onSaved}
            trigger={
              <Button size="sm" className="shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
                <HugeiconsIcon icon={Add01Icon} className="h-4 w-4 mr-1.5" strokeWidth={1.5} aria-hidden={true} />{' '}
                Agregar hito
              </Button>
            }
          />
        )}
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon={Flag01Icon}
          title="Sin hitos de seguimiento"
          subtitle={
            <EmptyExplainer
              what="Los hitos son los próximos pasos de la iniciativa: qué viene, en qué estado está (en desarrollo, en paralelo, en producción) y cuándo se promueve a producción."
              whyItMatters="Dan visibilidad de la hoja de ruta en un solo lugar. Muestran el avance real y los compromisos de fecha, sin depender de datos dispersos en otras vistas."
              ifEmpty="No queda claro qué sigue ni cuándo. El seguimiento se vuelve difícil de leer y se pierde la trazabilidad del progreso."
              whenDone="Tenemos una hoja de ruta clara y con fechas: cualquiera entiende el estado de avance y los próximos hitos de la iniciativa."
            />
          }
        />
      ) : (
        <ul className="space-y-3">
          {list.map((m) => (
            <li key={m.id}>
              <Card className="shadow-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-sm">{m.title}</p>
                        {m.status && <StatusBadge status={m.status} />}
                      </div>
                      {m.promoted_at && (
                        <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                          <HugeiconsIcon
                            icon={Calendar03Icon}
                            className="h-3.5 w-3.5"
                            strokeWidth={1.5}
                            aria-hidden={true}
                          />
                          Promoción a producción: {fmtDate(m.promoted_at)}
                        </p>
                      )}
                      {m.description && (
                        <p className="mt-1.5 text-sm text-muted-foreground whitespace-pre-line">{m.description}</p>
                      )}
                    </div>
                    {canEdit && (
                      <div className="flex shrink-0 items-center gap-1">
                        <MilestoneDialog
                          id={id}
                          list={list}
                          item={m}
                          onSaved={onSaved}
                          trigger={
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                              aria-label={`Editar hito ${m.title}`}
                              title="Editar">
                              <HugeiconsIcon
                                icon={PencilEdit02Icon}
                                className="h-3.5 w-3.5"
                                strokeWidth={1.5}
                                aria-hidden={true}
                              />
                            </Button>
                          }
                        />
                        <DeleteButton label={m.title} kind="hito" onConfirm={() => remove(m)} />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// DeleteButton: botón de ícono con confirmación, compartido por ambas secciones.
function DeleteButton({ label, kind, onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-muted-foreground hover:text-destructive"
          aria-label={`Eliminar ${kind} ${label}`}
          title="Eliminar">
          <HugeiconsIcon icon={Delete02Icon} className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden={true} />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">Eliminar {kind}</AlertDialogTitle>
          <AlertDialogDescription>
            ¿Deseas eliminar <span className="font-medium text-foreground">{label}</span> de la lista? Esta acción queda
            registrada en la auditoría de la iniciativa.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={onConfirm}>
            Eliminar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
